/**
 * Registration Request API Service
 *
 * API methods for registration request operations including submission,
 * token validation, and registration completion.
 *
 * These are public endpoints (no authentication required).
 */

import axios from 'axios';
import type {
  CreateRegistrationRequestDTO,
  CompleteRegistrationDTO,
  TokenValidationResponse,
  CompleteRegistrationResponse,
  RegistrationRequest,
} from '../types/registration-request.types';

// Base URL for API calls
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

// Admin API base URL for public submission
const ADMIN_API_BASE_URL = import.meta.env.VITE_ADMIN_API_BASE_URL || 'http://localhost:4000';

// ============================================================================
// REGISTRATION REQUEST SERVICE
// ============================================================================

export const registrationRequestService = {
  /**
   * Submit a new registration request (public endpoint)
   * Sends to admin-api for platform admin review
   */
  submitRequest: async (data: CreateRegistrationRequestDTO): Promise<{ message: string; data: RegistrationRequest }> => {
    const response = await axios.post<{ success: boolean; data: RegistrationRequest; message: string }>(
      `${ADMIN_API_BASE_URL}/registration-requests`,
      data,
      {
        headers: { 'Content-Type': 'application/json' },
        timeout: 10000,
      }
    );
    return {
      message: response.data.message || 'Registration request submitted successfully',
      data: response.data.data,
    };
  },

  /**
   * Validate a registration invitation token (public endpoint)
   * Used by frontend to check if token is valid before showing registration form
   */
  validateToken: async (token: string): Promise<TokenValidationResponse> => {
    const response = await axios.get<{ success: boolean; data: TokenValidationResponse }>(
      `${API_BASE_URL}/registration-requests/validate/${token}`,
      {
        headers: { 'Content-Type': 'application/json' },
        timeout: 10000,
      }
    );
    return response.data.data;
  },

  /**
   * Complete registration from approved token (public endpoint)
   * Creates tenant and admin user from pre-approved registration request
   */
  completeRegistration: async (
    token: string,
    data: CompleteRegistrationDTO
  ): Promise<{ message: string; data: CompleteRegistrationResponse }> => {
    const response = await axios.post<{ success: boolean; data: CompleteRegistrationResponse; message: string }>(
      `${API_BASE_URL}/registration-requests/complete/${token}`,
      data,
      {
        headers: { 'Content-Type': 'application/json' },
        timeout: 30000, // Longer timeout for registration
      }
    );
    return {
      message: response.data.message || 'Registration completed successfully',
      data: response.data.data,
    };
  },
};

export default registrationRequestService;
